import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, ChevronDown, User, Users, Briefcase, Building2, Mail, ArrowLeft,
  ArrowUp, Search, X, ChevronsUpDown, Filter, ChevronLeft
} from 'lucide-react';
import { useUserProfile } from '../hooks/useUserProfile';
import { useApiClient } from '../hooks/useApiClient';
import { ReporteeInfo as ReporteeInfoType } from '../types/userProfile';
import { FeedbackRequest } from '../types/feedback';
import '../styles/feedback.css';

const ITEMS_PER_PAGE = 100;
const MONTH_OPTIONS = [
  { label: 'Jan', value: '1' },
  { label: 'Feb', value: '2' },
  { label: 'Mar', value: '3' },
  { label: 'Apr', value: '4' },
  { label: 'May', value: '5' },
  { label: 'Jun', value: '6' },
  { label: 'Jul', value: '7' },
  { label: 'Aug', value: '8' },
  { label: 'Sep', value: '9' },
  { label: 'Oct', value: '10' },
  { label: 'Nov', value: '11' },
  { label: 'Dec', value: '12' }
];

interface TreeNodeProps {
  name: string;
  employeeId: string;
  jobTitle: string;
  department: string;
  email: string;
  isManager?: boolean;
  isCurrentUser?: boolean;
  children?: ReporteeInfoType[];
  level?: number;
  forceExpanded?: boolean;
  searchTerm?: string;
  highlightMatch?: boolean;
  onDrillDown?: (node: ReporteeInfoType) => void;
  onViewTeamConnects?: (node: ReporteeInfoType) => void;
  resolvedReportsCache?: Record<string, ReporteeInfoType[]>;
}

function flattenReportees(reportees: ReporteeInfoType[]): ReporteeInfoType[] {
  const flattened: ReporteeInfoType[] = [];

  const walk = (nodes: ReporteeInfoType[]) => {
    nodes.forEach((node) => {
      flattened.push(node);
      if (node.directReports && node.directReports.length > 0) {
        walk(node.directReports);
      }
    });
  };

  walk(reportees);
  return flattened;
}

function TreeNode({ 
  name, 
  employeeId, 
  jobTitle, 
  department, 
  email, 
  isManager = false, 
  isCurrentUser = false,
  children = [],
  level = 0,
  forceExpanded,
  searchTerm = '',
  highlightMatch = false,
  onDrillDown,
  onViewTeamConnects,
  resolvedReportsCache
}: TreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(isCurrentUser);
  const hasChildren = children && children.length > 0;

  // Only show expand/connects buttons when cache has CONFIRMED non-empty reports.
  // Default to false (hidden) until the pre-fetch resolves.
  const cachedReports = resolvedReportsCache?.[employeeId];
  const hasKnownReports = cachedReports !== undefined && cachedReports.length > 0;

  // Handle force expand/collapse
  useEffect(() => {
    if (forceExpanded !== undefined) {
      setIsExpanded(forceExpanded);
    }
  }, [forceExpanded]);

  // Highlight matching text
  const highlightText = (text: string) => {
    if (!searchTerm || !highlightMatch) return text;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark>
      ) : part
    );
  };
  
  return (
    <div className={`${level > 0 ? 'ml-8 border-l-2 border-slate-200 pl-4' : ''}`}>
      <div
        onClick={!isCurrentUser && !isManager && hasKnownReports
          ? (hasChildren
              ? () => setIsExpanded(prev => !prev)
              : () => onDrillDown?.({ employeeId, displayName: name, email, jobTitle, department, directReports: children }))
          : undefined}
        className={`
          relative flex items-start gap-4 p-4 rounded-xl mb-3 transition-all duration-200
          ${!isCurrentUser && !isManager && hasKnownReports ? 'cursor-pointer' : ''}
          ${isCurrentUser 
            ? 'bg-gradient-to-r from-pink-50 to-purple-50 border-2 border-pink-300 shadow-md' 
            : isManager 
              ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200'
              : 'bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm'
          }
        `}
      >
        {/* Connector line for non-root nodes */}
        {level > 0 && (
          <div className="absolute -left-4 top-1/2 w-4 h-px bg-slate-200" />
        )}
        
        {/* Expand/Collapse button — only shown when employee is confirmed to have direct reports */}
        {!isCurrentUser && !isManager && hasKnownReports && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) setIsExpanded(!isExpanded);
              else onDrillDown?.({ employeeId, displayName: name, email, jobTitle, department, directReports: children });
            }}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors mt-1"
            title={hasChildren ? (isExpanded ? 'Collapse' : 'Expand') : 'Explore team'}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-slate-600" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-600" />
            )}
          </button>
        )}
        
        {/* Avatar */}
        <div className={`
          flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center
          ${isCurrentUser 
            ? 'bg-gradient-to-br from-pink-500 to-purple-500' 
            : isManager 
              ? 'bg-gradient-to-br from-blue-500 to-indigo-500'
              : 'bg-gradient-to-br from-slate-400 to-slate-500'
          }
        `}>
          <User className="w-6 h-6 text-white" />
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-900 text-lg">{highlightText(name)}</h3>
            {isCurrentUser && (
              <span className="px-2 py-0.5 bg-pink-500 text-white text-xs font-medium rounded-full">
                You
              </span>
            )}
            {isManager && !isCurrentUser && (
              <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-medium rounded-full">
                Manager
              </span>
            )}
          </div>
          
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <span className="font-medium text-slate-500 w-20">ID:</span>
              <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">{highlightText(employeeId)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Briefcase className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>{highlightText(jobTitle || 'N/A')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>{highlightText(department || 'N/A')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <a href={`mailto:${email}`} onClick={(e) => e.stopPropagation()} className="text-pink-600 hover:underline truncate">
                {highlightText(email)}
              </a>
            </div>
          </div>
          
          {hasChildren && (
            <div className="mt-2 flex items-center gap-1 text-sm text-slate-500">
              <Users className="w-4 h-4" />
              <span>{children.length} direct report{children.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Only show for nodes confirmed to have direct reports */}
          {!isCurrentUser && !isManager && hasKnownReports && (
            <div className="mt-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewTeamConnects?.({ employeeId, displayName: name, email, jobTitle, department, directReports: children });
                }}
                className="text-sm font-semibold text-pink-500 hover:text-pink-600 flex items-center gap-1 px-3 py-1.5 rounded-lg border border-pink-200 hover:border-pink-400 hover:bg-pink-50 transition-all"
              >
                View team connects info <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="mt-2">
          {children.map((child, index) => (
            <TreeNode
              key={child.employeeId || index}
              name={child.displayName}
              employeeId={child.employeeId}
              jobTitle={child.jobTitle}
              department={child.department}
              email={child.email}
              children={child.directReports || []}
              level={level + 1}
              forceExpanded={forceExpanded}
              searchTerm={searchTerm}
              highlightMatch={highlightMatch}
              onDrillDown={onDrillDown}
              onViewTeamConnects={onViewTeamConnects}
              resolvedReportsCache={resolvedReportsCache}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Slide-in panel for team connects chart ───────────────────────────────────
interface TeamConnectsPanelProps {
  node: ReporteeInfoType;
  onClose: () => void;
  apiCall: (url: string, options?: RequestInit) => Promise<Response>;
}

function TeamConnectsPanel({ node, onClose, apiCall }: TeamConnectsPanelProps) {
  const [visible, setVisible] = useState(false);
  const [directReports, setDirectReports] = useState<ReporteeInfoType[]>([]);
  const [requestsMap, setRequestsMap] = useState<Record<string, FeedbackRequest[]>>({});
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [panelMonth, setPanelMonth] = useState('all');
  const [panelYear, setPanelYear] = useState('all');

  // Trigger slide-in on mount
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  // Fetch direct reports
  useEffect(() => {
    const load = async () => {
      if (node.directReports && node.directReports.length > 0) {
        setDirectReports(node.directReports);
        setIsLoadingReports(false);
        return;
      }
      setIsLoadingReports(true);
      try {
        const res = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/direct-reports?upn=${encodeURIComponent(node.email)}`
        );
        if (res.ok) {
          const result = await res.json();
          setDirectReports(result?.data ?? []);
        }
      } catch {}
      setIsLoadingReports(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.employeeId]);

  // Fetch connect data for each direct report
  useEffect(() => {
    if (!directReports.length) return;
    const fetchCounts = async () => {
      setIsLoadingCounts(true);
      const entries = await Promise.all(
        directReports.map(async (r) => {
          try {
            const res = await apiCall(
              `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/my-feedback/${encodeURIComponent(r.employeeId)}`
            );
            if (!res.ok) return [r.employeeId, []] as const;
            const result = await res.json();
            return [r.employeeId, result?.data ?? []] as const;
          } catch {
            return [r.employeeId, []] as const;
          }
        })
      );
      setRequestsMap(Object.fromEntries(entries));
      setIsLoadingCounts(false);
    };
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directReports]);

  const availableYears = useMemo(() => {
    const s = new Set<number>();
    Object.values(requestsMap).forEach(reqs =>
      reqs.forEach(r => { const d = new Date(r.requestDate); if (!Number.isNaN(d.getTime())) s.add(d.getFullYear()); })
    );
    const y = Array.from(s).sort((a, b) => b - a);
    if (!y.length) y.push(new Date().getFullYear());
    return y;
  }, [requestsMap]);

  const chartData = useMemo(() => {
    return directReports
      .map(r => {
        const requests = (requestsMap[r.employeeId] ?? []).filter(req => {
          if ((req.status || '').toLowerCase() === 'draft') return false;
          const d = new Date(req.requestDate);
          if (Number.isNaN(d.getTime())) return false;
          if (panelYear !== 'all' && d.getFullYear() !== Number(panelYear)) return false;
          if (panelMonth !== 'all' && d.getMonth() + 1 !== Number(panelMonth)) return false;
          return true;
        });
        const inProgressCount = requests.filter(req => req.status === 'in_progress').length;
        const completedCount = requests.filter(req => req.status === 'completed').length;
        const revisionCount = requests.filter(req => req.status === 'revision_requested').length;
        return {
          employeeId: r.employeeId,
          name: r.displayName,
          count: requests.length,
          inProgressCount,
          completedCount,
          revisionCount,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [directReports, requestsMap, panelMonth, panelYear]);

  const maxCount = useMemo(() => Math.max(...chartData.map(d => d.count), 1), [chartData]);
  const hasActiveFilters = panelMonth !== 'all' || panelYear !== 'all';

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Slide-in panel */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Panel header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200 bg-gradient-to-r from-pink-50 to-purple-50 flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 truncate">{node.displayName}</h2>
            </div>
            <p className="text-sm text-slate-500 ml-11">{node.jobTitle}{node.department ? ` · ${node.department}` : ''}</p>
            <p className="text-xs text-slate-400 font-mono ml-11 mt-0.5">{node.employeeId}</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/70 rounded-lg transition-colors flex-shrink-0 ml-2"
            aria-label="Close panel"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Panel body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-5">
            <h3 className="text-base font-semibold text-slate-800">Team Connect Status</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              All submitted connects across {node.displayName}&apos;s direct reports
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-600 mb-4">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" />In Progress</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Completed</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" />Revision</span>
          </div>

          {/* Date filters */}
          <div className="grid grid-cols-2 gap-2.5 mb-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Year</label>
              <select
                value={panelYear}
                onChange={e => {
                  const val = e.target.value;
                  setPanelYear(val);
                  if (val === 'all') setPanelMonth('all');
                }}
                className="w-full px-2.5 py-1.5 text-sm bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
              >
                <option value="all">All Years</option>
                {availableYears.map(y => <option key={y} value={String(y)}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${panelYear === 'all' ? 'text-slate-400' : 'text-slate-500'}`}>
                Month {panelYear === 'all' && <span className="italic">(year first)</span>}
              </label>
              <select
                disabled={panelYear === 'all'}
                value={panelMonth}
                onChange={e => setPanelMonth(e.target.value)}
                className={`w-full px-2.5 py-1.5 text-sm bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 ${
                  panelYear === 'all' ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <option value="all">All Months</option>
                {MONTH_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <button
              onClick={() => { setPanelMonth('all'); setPanelYear('all'); }}
              className="text-xs text-pink-600 hover:text-pink-700 font-medium mb-4 block"
            >
              Clear filters
            </button>
          )}

          {/* Chart area */}
          {isLoadingReports || isLoadingCounts ? (
            <div className="space-y-3 mt-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : directReports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Users className="w-10 h-10 mb-2" />
              <p className="text-sm">No direct reports found</p>
            </div>
          ) : (
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/40">
              <div className="flex items-end gap-4 overflow-x-auto pb-2" style={{ minHeight: '200px' }}>
                {chartData.map(item => {
                  const heightPct = (item.count / maxCount) * 100;
                  return (
                    <div key={item.employeeId} className="min-w-[80px] flex flex-col items-center gap-1.5">
                      <span className="text-sm font-semibold text-emerald-700 tabular-nums">{item.count}</span>
                      <div className="w-10 h-[150px] flex flex-col justify-end overflow-hidden rounded-t-md bg-slate-100" title={`${item.name}: ${item.count} connects`}>
                        <div className="w-full flex flex-col-reverse transition-all duration-500" style={{ height: `${Math.max(heightPct, item.count > 0 ? 8 : 2)}%` }}>
                          {item.inProgressCount > 0 && (
                            <div className="w-full bg-blue-500" style={{ height: `${(item.inProgressCount / item.count) * 100}%` }} title={`${item.inProgressCount} in progress`} />
                          )}
                          {item.completedCount > 0 && (
                            <div className="w-full bg-emerald-500" style={{ height: `${(item.completedCount / item.count) * 100}%` }} title={`${item.completedCount} completed`} />
                          )}
                          {item.revisionCount > 0 && (
                            <div className="w-full bg-amber-500" style={{ height: `${(item.revisionCount / item.count) * 100}%` }} title={`${item.revisionCount} revision requested`} />
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-700 text-center leading-tight break-words max-w-[80px]" title={item.name}>
                        {item.name}
                      </p>
                      <p className="text-[10px] text-slate-400 font-mono">{item.employeeId}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
function ScrollToTopButton() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <button
      onClick={scrollToTop}
      className={`
        fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full
        bg-gradient-to-r from-pink-500 to-purple-600 text-white
        shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40
        flex items-center justify-center
        transition-all duration-300 hover:scale-110
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}
      `}
      aria-label="Scroll to top"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}

export function ReporteeInfoPage() {
  const { userProfile, loading, error, fetchUserProfile } = useUserProfile();
  const { apiCall } = useApiClient();
  const [isLoaded, setIsLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedJobTitle, setSelectedJobTitle] = useState<string>('all');
  const [forceExpanded, setForceExpanded] = useState<boolean | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [chartOpen, setChartOpen] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedChartManagerId, setSelectedChartManagerId] = useState<string>('');
  const [selectedChartFilterId, setSelectedChartFilterId] = useState<string>('');
  const [viewTeamSearch, setViewTeamSearch] = useState('');
  const [viewTeamOpen, setViewTeamOpen] = useState(false);
  const viewTeamRef = React.useRef<HTMLDivElement>(null);
  const [reporteeRequestsMap, setReporteeRequestsMap] = useState<Record<string, FeedbackRequest[]>>({});
  const [drillStack, setDrillStack] = useState<ReporteeInfoType[]>([]);
  // Cache of lazily-fetched direct reports keyed by employeeId.
  // In production the Graph API only returns L1 reports; deeper levels are fetched on demand.
  const [resolvedReportsCache, setResolvedReportsCache] = useState<Record<string, ReporteeInfoType[]>>({});
  const [isFetchingDrillData, setIsFetchingDrillData] = useState(false);
  // Track which employeeIds have already had their connect counts fetched so we never double-fetch.
  const fetchedCountIdsRef = useRef<Set<string>>(new Set());
  const [panelNode, setPanelNode] = useState<ReporteeInfoType | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      await fetchUserProfile();
      setIsLoaded(true);
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fetch direct reports for all L1 employees on load so we know who has sub-reports.
  // This drives the visibility of expand buttons, "View connects" buttons, and the View Team dropdown.
  useEffect(() => {
    if (!userProfile?.directReports?.length) return;
    const toFetch = userProfile.directReports.filter(
      r => r.email && resolvedReportsCache[r.employeeId] === undefined
    );
    if (!toFetch.length) return;
    toFetch.forEach(async (report) => {
      try {
        const response = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/direct-reports?upn=${encodeURIComponent(report.email)}`
        );
        const result = response.ok ? await response.json() : { data: [] };
        setResolvedReportsCache(prev => ({ ...prev, [report.employeeId]: result?.data ?? [] }));
      } catch {
        setResolvedReportsCache(prev => ({ ...prev, [report.employeeId]: [] }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.directReports]);

  // Close View Team dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (viewTeamRef.current && !viewTeamRef.current.contains(e.target as Node)) {
        setViewTeamOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const focusDirectReports = useMemo(() => {
    if (drillStack.length > 0) {
      const node = drillStack[drillStack.length - 1];
      // Prefer lazily-fetched data (production) over whatever was already in the node
      // (which is always [] from the Graph API because the backend only returns L1).
      return resolvedReportsCache[node.employeeId] ?? node.directReports ?? [];
    }
    return userProfile?.directReports || [];
  }, [drillStack, resolvedReportsCache, userProfile?.directReports]);

  // Get unique departments and job titles for filters
  const { departments, jobTitles } = useMemo(() => {
    if (!focusDirectReports.length) return { departments: [], jobTitles: [] };
    const deptSet = new Set<string>();
    const titleSet = new Set<string>();
    focusDirectReports.forEach(r => {
      if (r.department) deptSet.add(r.department);
      if (r.jobTitle) titleSet.add(r.jobTitle);
    });
    return { 
      departments: Array.from(deptSet).sort(), 
      jobTitles: Array.from(titleSet).sort() 
    };
  }, [focusDirectReports]);

  // Filter direct reports based on search and filters
  const filteredReports = useMemo(() => {
    if (!focusDirectReports.length) return [];
    
    return focusDirectReports.filter(report => {
      const matchesSearch = !searchTerm || 
        report.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (report.jobTitle && report.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (report.department && report.department.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesDepartment = selectedDepartment === 'all' || report.department === selectedDepartment;
      const matchesJobTitle = selectedJobTitle === 'all' || report.jobTitle === selectedJobTitle;
      
      return matchesSearch && matchesDepartment && matchesJobTitle;
    });
  }, [focusDirectReports, searchTerm, selectedDepartment, selectedJobTitle]);

  // Pagination
  const totalPages = Math.ceil(filteredReports.length / ITEMS_PER_PAGE);
  const paginatedReports = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredReports.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredReports, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedDepartment, selectedJobTitle]);

  // When the focused level changes (initial load OR drill-down), fetch connect counts
  // for any reportees whose counts are not yet in reporteeRequestsMap.
  // Uses a ref so we never issue duplicate requests even if this effect re-runs.
  useEffect(() => {
    const fetchMissingCounts = async () => {
      const missing = focusDirectReports
        .map(r => r.employeeId)
        .filter(id => id && !fetchedCountIdsRef.current.has(id));

      if (!missing.length) return;

      // Mark as fetching immediately to prevent concurrent duplicate calls.
      missing.forEach(id => fetchedCountIdsRef.current.add(id));

      const entries = await Promise.all(
        missing.map(async (reporteeId) => {
          try {
            const response = await apiCall(
              `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/my-feedback/${encodeURIComponent(reporteeId)}`
            );
            if (!response.ok) return [reporteeId, []] as const;
            const result = await response.json();
            const requests: FeedbackRequest[] = result?.data || [];
            return [reporteeId, requests] as const;
          } catch {
            return [reporteeId, []] as const;
          }
        })
      );

      setReporteeRequestsMap(prev => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    };

    fetchMissingCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDirectReports]);

  const handleExpandAll = () => setForceExpanded(true);
  const handleCollapseAll = () => setForceExpanded(false);
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDepartment('all');
    setSelectedJobTitle('all');
  };

  const clearConnectFilters = () => {
    setSelectedMonth('all');
    setSelectedYear('all');
    setSelectedChartManagerId('');
    setSelectedChartFilterId('');
    setViewTeamSearch('');
  };

  const handleDrillDown = async (node: ReporteeInfoType) => {
    setDrillStack(prev => [...prev, node]);
    setSearchTerm('');
    setSelectedDepartment('all');
    setSelectedJobTitle('all');
    setCurrentPage(1);
    setSelectedChartManagerId('');
    setSelectedChartFilterId('');
    setViewTeamSearch('');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // If direct reports are already resolved (mock data or previously fetched) skip the API call.
    const alreadyResolved =
      resolvedReportsCache[node.employeeId] !== undefined ||
      (node.directReports !== undefined && node.directReports.length > 0);

    if (!alreadyResolved) {
      setIsFetchingDrillData(true);
      try {
        const response = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/direct-reports?upn=${encodeURIComponent(node.email)}`
        );
        if (response.ok) {
          const result = await response.json();
          const reports: ReporteeInfoType[] = result?.data ?? [];
          setResolvedReportsCache(prev => ({ ...prev, [node.employeeId]: reports }));
        } else {
          // Endpoint failed — fall back to empty so UI shows "no direct reports"
          setResolvedReportsCache(prev => ({ ...prev, [node.employeeId]: [] }));
        }
      } catch {
        setResolvedReportsCache(prev => ({ ...prev, [node.employeeId]: [] }));
      } finally {
        setIsFetchingDrillData(false);
      }
    }
  };

  const handleBreadcrumbNavigate = (index: number) => {
    setDrillStack(index < 0 ? [] : drillStack.slice(0, index + 1));
    setSearchTerm('');
    setSelectedDepartment('all');
    setSelectedJobTitle('all');
    setCurrentPage(1);
    setSelectedChartManagerId('');
    setSelectedChartFilterId('');
    setViewTeamSearch('');
  };

  // When a manager is selected in "View Team" dropdown and we don't have their
  // sub-reports cached yet, lazy-fetch them so the chart can render.
  useEffect(() => {
    if (!selectedChartManagerId) return;
    if (resolvedReportsCache[selectedChartManagerId] !== undefined) return;
    const all = flattenReportees(userProfile?.directReports || []);
    const mgr = all.find(m => m.employeeId === selectedChartManagerId)
              ?? (userProfile?.directReports || []).find(r => r.employeeId === selectedChartManagerId);
    if (!mgr?.email) return;
    (async () => {
      try {
        const response = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/api/user/direct-reports?upn=${encodeURIComponent(mgr.email)}`
        );
        const result = response.ok ? await response.json() : { data: [] };
        setResolvedReportsCache(prev => ({ ...prev, [selectedChartManagerId]: result?.data ?? [] }));
      } catch {
        setResolvedReportsCache(prev => ({ ...prev, [selectedChartManagerId]: [] }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChartManagerId]);

  const hasActiveFilters = searchTerm || selectedDepartment !== 'all' || selectedJobTitle !== 'all';
  const hasActiveConnectFilters = selectedMonth !== 'all' || selectedYear !== 'all' || !!selectedChartManagerId || !!selectedChartFilterId;

  const availableYears = useMemo(() => {
    const yearSet = new Set<number>();
    Object.values(reporteeRequestsMap).forEach((requests) => {
      requests.forEach((request) => {
        const parsedDate = new Date(request.requestDate);
        if (!Number.isNaN(parsedDate.getTime())) {
          yearSet.add(parsedDate.getFullYear());
        }
      });
    });

    const years = Array.from(yearSet).sort((a, b) => b - a);
    if (years.length === 0) {
      years.push(new Date().getFullYear());
    }

    return years;
  }, [reporteeRequestsMap]);

  const managersWithReports = useMemo(() => {
    if (!userProfile) return [];
    // Only include L1 direct reports that the cache has confirmed have sub-reports.
    const rootL1 = userProfile.directReports || [];
    const seen = new Set<string>();
    return rootL1.filter(r => {
      if (seen.has(r.employeeId)) return false;
      seen.add(r.employeeId);
      return (resolvedReportsCache[r.employeeId]?.length ?? 0) > 0;
    });
  }, [userProfile?.directReports, resolvedReportsCache]);

  const chartFocusReports = useMemo(() => {
    if (!selectedChartManagerId) return focusDirectReports;
    const fromCache = resolvedReportsCache[selectedChartManagerId];
    if (fromCache) return fromCache;
    const all = flattenReportees(userProfile?.directReports || []);
    const mgr = all.find(r => r.employeeId === selectedChartManagerId);
    return mgr?.directReports || [];
  }, [selectedChartManagerId, focusDirectReports, resolvedReportsCache, userProfile?.directReports]);

  // When the selected chart manager changes, ensure their direct reports' counts are fetched.
  useEffect(() => {
    const fetchChartCounts = async () => {
      const missing = chartFocusReports
        .map(r => r.employeeId)
        .filter(id => id && !fetchedCountIdsRef.current.has(id));
      if (!missing.length) return;
      missing.forEach(id => fetchedCountIdsRef.current.add(id));
      const entries = await Promise.all(
        missing.map(async (reporteeId) => {
          try {
            const response = await apiCall(
              `${process.env.REACT_APP_API_BASE_URL}/api/FeedbackRequests/my-feedback/${encodeURIComponent(reporteeId)}`
            );
            if (!response.ok) return [reporteeId, []] as const;
            const result = await response.json();
            const requests: FeedbackRequest[] = result?.data || [];
            return [reporteeId, requests] as const;
          } catch {
            return [reporteeId, []] as const;
          }
        })
      );
      setReporteeRequestsMap(prev => ({ ...prev, ...Object.fromEntries(entries) }));
    };
    fetchChartCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartFocusReports]);

  const completedCounts = useMemo(() => {
    return Object.fromEntries(
      Object.entries(reporteeRequestsMap).map(([reporteeId, requests]) => {
        const completedCount = requests.filter((request) => {
          const parsedDate = new Date(request.requestDate);
          if (Number.isNaN(parsedDate.getTime())) {
            return false;
          }

          if (selectedYear !== 'all' && parsedDate.getFullYear() !== Number(selectedYear)) {
            return false;
          }

          if (selectedMonth !== 'all' && parsedDate.getMonth() + 1 !== Number(selectedMonth)) {
            return false;
          }

          return true;
        }).length;

        return [reporteeId, completedCount] as const;
      })
    );
  }, [reporteeRequestsMap, selectedMonth, selectedYear]);

  // Status-wise counts per reportee (filtered by month/year)
  const statusCounts = useMemo(() => {
    return Object.fromEntries(
      Object.entries(reporteeRequestsMap).map(([reporteeId, requests]) => {
        const filtered = requests.filter((request) => {
          if (request.status === 'draft') return false;
          const parsedDate = new Date(request.requestDate);
          if (Number.isNaN(parsedDate.getTime())) return false;
          if (selectedYear !== 'all' && parsedDate.getFullYear() !== Number(selectedYear)) return false;
          if (selectedMonth !== 'all' && parsedDate.getMonth() + 1 !== Number(selectedMonth)) return false;
          return true;
        });
        return [reporteeId, {
          in_progress: filtered.filter(r => r.status === 'in_progress').length,
          completed: filtered.filter(r => r.status === 'completed').length,
          revision_requested: filtered.filter(r => r.status === 'revision_requested').length,
          total: filtered.length,
        }] as const;
      })
    );
  }, [reporteeRequestsMap, selectedMonth, selectedYear]);

  // Filtered requests per reportee for inline drilldown
  const filteredRequestsMap = useMemo(() => {
    return Object.fromEntries(
      Object.entries(reporteeRequestsMap).map(([reporteeId, requests]) => {
        const filtered = requests.filter((request) => {
          if (request.status === 'draft') return false;
          const parsedDate = new Date(request.requestDate);
          if (Number.isNaN(parsedDate.getTime())) return false;
          if (selectedYear !== 'all' && parsedDate.getFullYear() !== Number(selectedYear)) return false;
          if (selectedMonth !== 'all' && parsedDate.getMonth() + 1 !== Number(selectedMonth)) return false;
          return true;
        });
        return [reporteeId, filtered] as const;
      })
    );
  }, [reporteeRequestsMap, selectedMonth, selectedYear]);

  const completedConnectChartData = useMemo(() => {
    if (!chartFocusReports.length) return [];

    const all = chartFocusReports
      .map((report) => {
        const counts = statusCounts[report.employeeId] ?? { in_progress: 0, completed: 0, revision_requested: 0, total: 0 };
        return {
          employeeId: report.employeeId,
          name: report.displayName,
          completedCount: completedCounts[report.employeeId] ?? 0,
          inProgressCount: counts.in_progress,
          revisionCount: counts.revision_requested,
          totalCount: counts.total,
        };
      })
      .sort((a, b) => b.totalCount - a.totalCount);

    if (selectedChartFilterId) {
      return all.filter(item => item.employeeId === selectedChartFilterId);
    }
    return all;
  }, [completedCounts, statusCounts, chartFocusReports, selectedChartFilterId]);

  const maxCompletedConnectCount = useMemo(() => {
    if (!completedConnectChartData.length) return 1;
    return Math.max(...completedConnectChartData.map((item) => item.totalCount), 1);
  }, [completedConnectChartData]);

  // Track which bar is expanded for inline drilldown
  const [expandedBarEmployeeId, setExpandedBarEmployeeId] = useState<string | null>(null);

  // Determine which employees are direct reports (actionable)
  const directReportIds = useMemo(() => {
    return new Set((userProfile?.directReports || []).map(r => r.employeeId));
  }, [userProfile?.directReports]);

  if (loading && !isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pt-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pt-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-600">Error loading profile: {error}</p>
            <Link 
              to="/dashboard" 
              className="inline-flex items-center gap-2 mt-4 text-pink-600 hover:text-pink-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pt-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
            <p className="text-yellow-700">No profile data available</p>
            <Link 
              to="/dashboard" 
              className="inline-flex items-center gap-2 mt-4 text-pink-600 hover:text-pink-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const focusNode = drillStack.length > 0 ? drillStack[drillStack.length - 1] : null;
  const hasDirectReports = userProfile.directReports && userProfile.directReports.length > 0;
  const focusHasDirectReports = focusDirectReports.length > 0;
  const totalReports = focusDirectReports.length;
  const showPagination = filteredReports.length > ITEMS_PER_PAGE;

  return (
    <div className="min-h-screen bg-slate-50 pt-24">
      <div className="mx-auto px-10 lg:px-20 xl:px-32 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-4 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          
          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl p-8 text-white shadow-xl">
            <div className="flex items-center gap-4 mb-2">
              <Users className="w-10 h-10" />
              <h1 className="text-3xl font-bold">Organization Hierarchy</h1>
            </div>
            <p className="text-white/80 ml-14">
              View your team structure and direct reports in a hierarchical tree view
            </p>
          </div>
        </div>

        {/* Summary Stats - Moved to top */}
        {focusHasDirectReports && (
          <>
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 mb-6">
              <button
                type="button"
                onClick={() => setChartOpen(!chartOpen)}
                className="w-full flex items-center justify-between group mb-1"
              >
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 text-left">Team Connect Overview</h2>
                  <p className="text-sm text-slate-500 mt-1 text-left">
                    {focusNode
                    ? `Comparison of connects across ${focusNode.displayName}'s direct reports`
                    : 'Comparison of connects across your direct reports'}
                  </p>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-slate-400 group-hover:text-slate-600 transition-transform duration-200 flex-shrink-0 ml-4 ${
                    chartOpen ? '' : '-rotate-90'
                  }`}
                />
              </button>

              {chartOpen && (
              <div className="mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedYear(val);
                      if (val === 'all') setSelectedMonth('all');
                    }}
                    className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
                  >
                    <option value="all">All Years</option>
                    {availableYears.map((year) => (
                      <option key={year} value={String(year)}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-xs font-medium mb-1 ${selectedYear === 'all' ? 'text-slate-400' : 'text-slate-500'}`}>
                    Month {selectedYear === 'all' && <span className="italic">(select year first)</span>}
                  </label>
                  <select
                    disabled={selectedYear === 'all'}
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className={`w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 ${
                      selectedYear === 'all' ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="all">All Months</option>
                    {MONTH_OPTIONS.map((month) => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Filter / View Team</label>
                  <div ref={viewTeamRef} className="relative">
                    {/* Trigger button */}
                    <button
                      type="button"
                      onClick={() => { setViewTeamOpen(o => !o); setViewTeamSearch(''); }}
                      className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-left text-sm flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
                    >
                      <span className="truncate">
                        {selectedChartFilterId
                          ? focusDirectReports.find(r => r.employeeId === selectedChartFilterId)?.displayName ?? 'Show All'
                          : selectedChartManagerId
                            ? `${managersWithReports.find(m => m.employeeId === selectedChartManagerId)?.displayName ?? ''}'s sub-team`
                            : focusNode ? `${focusNode.displayName}'s team (All)` : 'Show All'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 ml-2 transition-transform duration-150 ${viewTeamOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Dropdown */}
                    {viewTeamOpen && (
                      <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                        {/* Search input */}
                        <div className="p-2 border-b border-slate-100">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                            <input
                              autoFocus
                              type="text"
                              placeholder="Search by name..."
                              value={viewTeamSearch}
                              onChange={e => setViewTeamSearch(e.target.value)}
                              className="w-full pl-7 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
                            />
                          </div>
                        </div>
                        {/* Options list */}
                        <div className="max-h-60 overflow-y-auto">
                          {/* Show All option */}
                          {('Show All').toLowerCase().includes(viewTeamSearch.toLowerCase()) && (
                            <button
                              type="button"
                              onClick={() => { setSelectedChartManagerId(''); setSelectedChartFilterId(''); setViewTeamOpen(false); setViewTeamSearch(''); }}
                              className={`w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 border-b border-slate-100 ${!selectedChartManagerId && !selectedChartFilterId ? 'font-semibold text-pink-600 bg-pink-50' : 'text-slate-700'}`}
                            >
                              Show All
                            </button>
                          )}
                          {/* All direct reportees */}
                          {focusDirectReports
                            .filter(r => r.displayName.toLowerCase().includes(viewTeamSearch.toLowerCase()) || r.employeeId.toLowerCase().includes(viewTeamSearch.toLowerCase()))
                            .map(report => {
                              const isManager = (resolvedReportsCache[report.employeeId]?.length ?? 0) > 0;
                              const isSelected = selectedChartFilterId === report.employeeId;
                              return (
                                <div key={report.employeeId} className={`flex items-center hover:bg-slate-50 ${isSelected ? 'bg-pink-50' : ''}`}>
                                  <button
                                    type="button"
                                    onClick={() => { setSelectedChartFilterId(report.employeeId); setSelectedChartManagerId(''); setViewTeamOpen(false); setViewTeamSearch(''); }}
                                    className={`flex-1 text-left px-3 py-2.5 text-sm ${isSelected ? 'font-semibold text-pink-600' : 'text-slate-700'}`}
                                  >
                                    <span>{report.displayName}</span>
                                    <span className="text-slate-400 ml-1.5 text-xs font-mono">{report.employeeId}</span>
                                    {isManager && (
                                      <span className="ml-2 px-1.5 py-0.5 bg-indigo-100 text-indigo-600 text-[10px] font-semibold rounded">Manager</span>
                                    )}
                                  </button>
                                  {isManager && (
                                    <button
                                      type="button"
                                      title={`View ${report.displayName}'s sub-team`}
                                      onClick={() => { setSelectedChartManagerId(report.employeeId); setSelectedChartFilterId(''); setViewTeamOpen(false); setViewTeamSearch(''); }}
                                      className="px-2 py-2.5 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
                                    >
                                      <Users className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              );
                            })
                          }
                          {focusDirectReports.filter(r => r.displayName.toLowerCase().includes(viewTeamSearch.toLowerCase()) || r.employeeId.toLowerCase().includes(viewTeamSearch.toLowerCase())).length === 0 &&
                            !('Show All').toLowerCase().includes(viewTeamSearch.toLowerCase()) && (
                            <p className="px-3 py-2 text-sm text-slate-400 italic">No matches</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-slate-500">
                  {selectedChartFilterId
                    ? `Filtered to ${focusDirectReports.find(r => r.employeeId === selectedChartFilterId)?.displayName ?? ''}`
                    : selectedChartManagerId
                      ? `Showing ${managersWithReports.find(m => m.employeeId === selectedChartManagerId)?.displayName ?? ''}'s sub-team connects`
                      : 'Showing connects by request date'}
                </p>
                {hasActiveConnectFilters && (
                  <button
                    onClick={clearConnectFilters}
                    className="text-sm text-pink-600 hover:text-pink-700 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/40">
                {/* Legend */}
                <div className="flex items-center gap-4 mb-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-500 inline-block"></span> In Progress</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block"></span> Completed</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-500 inline-block"></span> Revision</span>
                </div>

                {/* Skeleton loading while data is being fetched */}
                {completedConnectChartData.length > 0 && completedConnectChartData.every(item => !(item.employeeId in reporteeRequestsMap)) ? (
                  <div className="flex flex-col gap-2.5">
                    {Array.from({ length: Math.min(completedConnectChartData.length, 8) }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 animate-pulse">
                        <div className="w-44 flex-shrink-0 text-right pr-2">
                          <div className="h-4 bg-slate-200 rounded w-28 ml-auto mb-1"></div>
                          <div className="h-3 bg-slate-100 rounded w-16 ml-auto"></div>
                        </div>
                        <div className="flex-1 h-8 bg-slate-100 rounded-md overflow-hidden">
                          <div className="h-full bg-slate-200 rounded-md" style={{ width: `${30 + i * 8}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (() => {
                  // Split chart data into direct ICs and sub-manager groups
                  const directICs = completedConnectChartData.filter(item => directReportIds.has(item.employeeId) && !(managersWithReports.some(m => m.employeeId === item.employeeId)));
                  const subManagerGroups = managersWithReports.map(mgr => ({
                    manager: mgr,
                    members: completedConnectChartData.filter(item => {
                      const mgrReports = resolvedReportsCache[mgr.employeeId] || [];
                      return mgrReports.some(r => r.employeeId === item.employeeId);
                    })
                  })).filter(g => g.members.length > 0);
                  // If no sub-manager grouping active (flat view or viewing sub-manager's team), show flat
                  const showGrouped = !selectedChartManagerId && subManagerGroups.length > 0;

                  const renderBar = (item: typeof completedConnectChartData[0], isDirectReport: boolean) => {
                    const total = item.totalCount;
                    const isZero = total === 0;
                    const maxCount = maxCompletedConnectCount;
                    const barPercent = maxCount > 0 ? (total / maxCount) * 100 : 0;
                    const barWidth = Math.max(barPercent, total > 0 ? 8 : 8);
                    const isExpanded = expandedBarEmployeeId === item.employeeId;

                    // Segment widths within the bar
                    const inProgressPct = total > 0 ? (item.inProgressCount / total) * 100 : 0;
                    const completedPct = total > 0 ? (item.completedCount / total) * 100 : 0;
                    const revisionPct = total > 0 ? (item.revisionCount / total) * 100 : 0;

                    return (
                      <div key={item.employeeId}>
                        <div
                          className="flex items-center gap-3 cursor-pointer hover:bg-slate-100 rounded-lg px-1 py-0.5 transition-colors"
                          onClick={() => setExpandedBarEmployeeId(isExpanded ? null : item.employeeId)}
                          title={`Click to ${isExpanded ? 'collapse' : 'view'} connects`}
                        >
                          {/* Name + ID */}
                          <div className="w-44 flex-shrink-0 text-right pr-2">
                            <p className="text-sm text-slate-700 truncate font-medium" title={item.name}>
                              {item.name}
                            </p>
                            <p className="text-[10px] text-slate-400 font-mono">{item.employeeId}</p>
                          </div>
                          {/* Stacked horizontal bar */}
                          <div className="flex-1 flex items-center gap-2">
                            <div className="flex-1 h-8 bg-slate-100 rounded-md overflow-hidden">
                              {isZero ? (
                                <div
                                  className="h-full rounded-md bg-gradient-to-r from-red-500 to-red-400 flex items-center justify-center px-2"
                                  style={{ width: '32px', minWidth: '32px' }}
                                >
                                  <span className="text-xs font-bold text-white">0</span>
                                </div>
                              ) : (
                                <div className="h-full flex" style={{ width: `${barWidth}%`, minWidth: '48px' }}>
                                  {item.inProgressCount > 0 && (
                                    <div
                                      className="h-full bg-blue-500 flex items-center justify-center"
                                      style={{ width: `${inProgressPct}%`, minWidth: '24px' }}
                                      title={`${item.inProgressCount} in progress`}
                                    >
                                      <span className="text-[10px] font-bold text-white">{item.inProgressCount}</span>
                                    </div>
                                  )}
                                  {item.completedCount > 0 && (
                                    <div
                                      className="h-full bg-emerald-500 flex items-center justify-center"
                                      style={{ width: `${completedPct}%`, minWidth: '24px' }}
                                      title={`${item.completedCount} completed`}
                                    >
                                      <span className="text-[10px] font-bold text-white">{item.completedCount}</span>
                                    </div>
                                  )}
                                  {item.revisionCount > 0 && (
                                    <div
                                      className="h-full bg-amber-500 flex items-center justify-center"
                                      style={{ width: `${revisionPct}%`, minWidth: '24px' }}
                                      title={`${item.revisionCount} revision requested`}
                                    >
                                      <span className="text-[10px] font-bold text-white">{item.revisionCount}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {/* Expand indicator */}
                            <span className="text-slate-400 w-4 flex-shrink-0">
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </span>
                          </div>
                        </div>
                        {/* Inline drilldown */}
                        {isExpanded && (
                          <div className="ml-48 mr-6 mt-1 mb-2 border border-slate-200 rounded-lg bg-white overflow-hidden">
                            {(filteredRequestsMap[item.employeeId] || []).length === 0 ? (
                              <p className="text-sm text-slate-400 p-3 text-center">No connects found</p>
                            ) : (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-50 text-slate-500 text-xs">
                                    <th className="text-left py-2 px-3 font-medium">Connect</th>
                                    <th className="text-left py-2 px-3 font-medium">Status</th>
                                    <th className="text-left py-2 px-3 font-medium">Date</th>
                                    <th className="text-right py-2 px-3 font-medium">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(filteredRequestsMap[item.employeeId] || []).map(req => {
                                    const statusColors: Record<string, string> = {
                                      in_progress: 'bg-blue-100 text-blue-700',
                                      completed: 'bg-emerald-100 text-emerald-700',
                                      revision_requested: 'bg-amber-100 text-amber-700',
                                    };
                                    const statusLabels: Record<string, string> = {
                                      in_progress: 'In Progress',
                                      completed: 'Completed',
                                      revision_requested: 'Revision',
                                    };
                                    const canAct = isDirectReport && req.status === 'in_progress';
                                    const link = canAct
                                      ? `/provide-manager-review/${req.id}`
                                      : `/view-feedback-request/${req.id}`;
                                    const label = canAct ? 'Add Comments' : 'View';
                                    return (
                                      <tr key={req.id} className="border-t border-slate-100 hover:bg-slate-50">
                                        <td className="py-2 px-3 text-slate-700 truncate max-w-[180px]">{req.connectName || 'Connect'}</td>
                                        <td className="py-2 px-3">
                                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${statusColors[req.status] || 'bg-slate-100 text-slate-600'}`}>
                                            {statusLabels[req.status] || req.status}
                                          </span>
                                        </td>
                                        <td className="py-2 px-3 text-slate-500 text-xs">{new Date(req.requestDate).toLocaleDateString()}</td>
                                        <td className="py-2 px-3 text-right">
                                          <Link
                                            to={link}
                                            className={`text-xs font-medium px-3 py-1 rounded-md transition-colors ${
                                              canAct
                                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}
                                          >
                                            {label}
                                          </Link>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  };

                  return (
                    <div className="flex flex-col gap-2.5">
                      {showGrouped ? (
                        <>
                          {/* Direct ICs section */}
                          {directICs.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-1">Your Direct Reports</p>
                              {directICs.map(item => renderBar(item, true))}
                            </div>
                          )}
                          {/* Sub-manager groups */}
                          {subManagerGroups.map(group => (
                            <div key={group.manager.employeeId}>
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 mt-3">
                                {group.manager.displayName}&rsquo;s Team ({group.members.length})
                              </p>
                              {group.members.map(item => renderBar(item, false))}
                            </div>
                          ))}
                        </>
                      ) : (
                        /* Flat list */
                        completedConnectChartData.map(item => renderBar(item, directReportIds.has(item.employeeId)))
                      )}
                      {completedConnectChartData.length === 0 && (
                        <p className="text-center text-sm text-slate-400 py-8">No connect data available</p>
                      )}
                    </div>
                  );
                })()}
              </div>
              </div>
              )}
            </div>
          </>
        )}

        {/* Search and Filter Controls - Only show if has reports */}
        {focusHasDirectReports && totalReports > 5 && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-4 mb-6">
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, ID, email, department, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                    showFilters || hasActiveFilters
                      ? 'bg-pink-50 border-pink-300 text-pink-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {hasActiveFilters && (
                    <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                  )}
                </button>
                
                <button
                  onClick={handleExpandAll}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:border-slate-300 transition-all"
                  title="Expand All"
                >
                  <ChevronsUpDown className="w-4 h-4" />
                  <span className="hidden sm:inline">Expand</span>
                </button>
                
                <button
                  onClick={handleCollapseAll}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:border-slate-300 transition-all"
                  title="Collapse All"
                >
                  <ChevronRight className="w-4 h-4" />
                  <span className="hidden sm:inline">Collapse</span>
                </button>
              </div>
            </div>

            {/* Filter Dropdowns */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Department</label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
                  >
                    <option value="all">All Departments ({departments.length})</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Job Title</label>
                  <select
                    value={selectedJobTitle}
                    onChange={(e) => setSelectedJobTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500"
                  >
                    <option value="all">All Roles ({jobTitles.length})</option>
                    {jobTitles.map(title => (
                      <option key={title} value={title}>{title}</option>
                    ))}
                  </select>
                </div>

                {hasActiveFilters && (
                  <div className="flex items-end">
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 text-pink-600 hover:text-pink-700 font-medium transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Results count */}
            {hasActiveFilters && (
              <div className="mt-3 text-sm text-slate-500">
                Showing {filteredReports.length} of {totalReports} direct reports
              </div>
            )}
          </div>
        )}

        {/* Tree View */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
          <div className="mb-6 pb-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-pink-500" />
              Team Structure
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {focusHasDirectReports 
                ? `${focusNode ? focusNode.displayName + ' has' : 'You have'} ${totalReports} direct report${totalReports !== 1 ? 's' : ''}`
                : `${focusNode ? focusNode.displayName + ' has' : 'You have'} no direct reports`
              }
            </p>
          </div>

          {/* Manager (if exists) - only at root level */}
          {drillStack.length === 0 && userProfile.manager && (
            <div className="mb-6">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                Your Manager
              </p>
              <TreeNode
                name={userProfile.manager.displayName}
                employeeId={userProfile.manager.employeeId}
                jobTitle={userProfile.manager.jobTitle}
                department={userProfile.manager.department}
                email={userProfile.manager.email}
                isManager={true}
              />
            </div>
          )}

          {/* Breadcrumb navigation when drilled down */}
          {drillStack.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-5 text-sm bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
              <button
                onClick={() => handleBreadcrumbNavigate(-1)}
                className="text-pink-500 hover:text-pink-600 font-medium hover:underline"
              >
                {userProfile.displayName} (You)
              </button>
              {drillStack.map((node, i) => (
                <React.Fragment key={node.employeeId}>
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  {i < drillStack.length - 1 ? (
                    <button
                      onClick={() => handleBreadcrumbNavigate(i)}
                      className="text-pink-500 hover:text-pink-600 font-medium hover:underline"
                    >
                      {node.displayName}
                    </button>
                  ) : (
                    <span className="font-semibold text-slate-700">{node.displayName}</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Current User with Direct Reports */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              {focusNode
                ? `${focusNode.displayName}'s Team`
                : focusHasDirectReports ? 'You & Your Team' : 'Your Profile'}
            </p>

            {/* Loading skeleton while lazily fetching direct reports after drill-down */}
            {isFetchingDrillData && (
              <div className="space-y-3 ml-8 mt-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />
                ))}
              </div>
            )}
            
            {/* Root/Focus Node */}
            <div className={`${focusHasDirectReports ? 'mb-4' : ''}`}>
              <TreeNode
                name={focusNode ? focusNode.displayName : userProfile.displayName}
                employeeId={focusNode ? focusNode.employeeId : userProfile.employeeId}
                jobTitle={focusNode ? focusNode.jobTitle : userProfile.jobTitle}
                department={focusNode ? focusNode.department : userProfile.department}
                email={focusNode ? focusNode.email : userProfile.email}
                isCurrentUser={true}
                children={hasActiveFilters ? [] : (showPagination ? paginatedReports : filteredReports)}
                forceExpanded={forceExpanded}
                searchTerm={searchTerm}
                highlightMatch={!!searchTerm}
                onDrillDown={handleDrillDown}
                onViewTeamConnects={setPanelNode}
                resolvedReportsCache={resolvedReportsCache}
              />
            </div>

            {/* Filtered Results (when search/filter active) */}
            {hasActiveFilters && filteredReports.length > 0 && (
              <div className="ml-8 border-l-2 border-slate-200 pl-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Filtered Results
                </p>
                {paginatedReports.map((report, index) => (
                  <TreeNode
                    key={report.employeeId || index}
                    name={report.displayName}
                    employeeId={report.employeeId}
                    jobTitle={report.jobTitle}
                    department={report.department}
                    email={report.email}
                    level={1}
                    forceExpanded={forceExpanded}
                    searchTerm={searchTerm}
                    highlightMatch={!!searchTerm}
                    onDrillDown={handleDrillDown}
                    onViewTeamConnects={setPanelNode}
                    resolvedReportsCache={resolvedReportsCache}
                  />
                ))}
              </div>
            )}

            {/* No Results Message */}
            {hasActiveFilters && filteredReports.length === 0 && (
              <div className="ml-8 p-6 text-center text-slate-500 bg-slate-50 rounded-xl">
                <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No team members match your search criteria</p>
                <button
                  onClick={clearFilters}
                  className="mt-2 text-pink-600 hover:text-pink-700 font-medium"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {showPagination && (
            <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {currentPage} of {totalPages} ({filteredReports.length} results)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll to Top Button */}
      <ScrollToTopButton />

      {/* Team connects slide-in panel */}
      {panelNode && (
        <TeamConnectsPanel
          node={panelNode}
          onClose={() => setPanelNode(null)}
          apiCall={apiCall}
        />
      )}
    </div>
  );
}
